/** 工程師：runtime_params 熱更新（runner 2–5s 內拉到，不重啟）。 */
import { useEffect, useState } from 'react';
import { getFleetConfig, putFleetParams } from '../../lib/fleetApi';
import {
  PARAM_FIELDS,
  PLATFORM_IDS,
  PLATFORM_LABEL,
  parseParamsText,
  readBooleanFlag,
  readPlatformFlag,
  writeBooleanFlag,
  writePlatformFlag,
  type ParamField,
} from './params-schema';

export function ParamsPage() {
  const [params, setParams] = useState<Record<string, unknown> | null>(null);
  const [text, setText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [meta, setMeta] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function applyDocument(next: Record<string, unknown>) {
    setParams(next);
    setText(JSON.stringify(next, null, 2));
    setJsonError(null);
  }

  async function load() {
    try {
      const cfg = await getFleetConfig();
      setVersion(cfg.version);
      applyDocument(cfg.params ?? {});
      setMeta(`${cfg.updatedAt ?? ''} · ${cfg.updatedBy ?? '—'}`);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setVersion(null);
      setParams(null);
      setText('');
      setJsonError(null);
      setMeta('');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function onText(value: string) {
    setText(value);
    const parsed = parseParamsText(value);
    if (parsed.ok) {
      setParams(parsed.params);
      setJsonError(null);
      return;
    }
    setJsonError(parsed.error);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const parsed = parseParamsText(text);
      if (!parsed.ok) {
        setError(parsed.error);
        return;
      }
      const res = await putFleetParams(parsed.params);
      setVersion(res.version);
      try {
        const cfg = await getFleetConfig();
        setVersion(cfg.version);
        if (cfg.params !== undefined) {
          applyDocument(cfg.params);
          setMeta(`${cfg.updatedAt ?? ''} · ${cfg.updatedBy ?? '—'}`);
        }
        setOk(`已寫入 v${cfg.version}；runner 下一輪拉設定即生效。`);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        setError(`已寫入 v${res.version}，讀回伺服器參數失敗：${detail}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const loaded = params !== null;
  const blocked = busy || (!loaded && error !== null);

  return (
    <div className="flex flex-col gap-3 p-3" data-testid="params-page">
      <header>
        <p className="text-[11px] uppercase tracking-widest text-text-tertiary">系統工程師</p>
        <h1 className="font-serif text-xl">運行參數</h1>
        <p className="mt-1 text-xs text-text-tertiary">
          {version === null
            ? error
              ? '參數尚未載入。'
              : '載入參數中…'
            : `目前版本 v${version}。${meta}。改完不需重啟容器。`}
        </p>
      </header>
      {error ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-danger" role="alert" data-testid="params-error">
          <span>{error}</span>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-xs text-text"
            onClick={() => {
              void load();
            }}
          >
            重試
          </button>
        </div>
      ) : null}
      {ok ? (
        <p className="text-sm text-success" data-testid="params-ok">
          {ok}
        </p>
      ) : null}
      {loaded ? (
        <section className="flex flex-col gap-4" data-testid="params-guided" aria-label="已知參數">
          {PARAM_FIELDS.map((field) => (
            <ParamFieldControl
              key={field.key}
              field={field}
              params={params}
              disabled={blocked}
              onChange={(next) => {
                applyDocument(next);
                setOk(null);
              }}
            />
          ))}
        </section>
      ) : null}
      <details className="rounded border border-border" data-testid="params-advanced">
        <summary className="cursor-pointer px-3 py-2 text-sm">進階 JSON</summary>
        <div className="flex flex-col gap-2 px-3 pb-3">
          <p className="text-xs text-text-tertiary">沒有列在上面的鍵只在這裡改。</p>
          {jsonError ? (
            <p className="text-sm text-danger" role="alert" data-testid="params-json-error">
              {jsonError}
            </p>
          ) : null}
          <textarea
            data-testid="params-json"
            className="min-h-[12rem] w-full rounded border border-border bg-surface p-3 font-mono text-xs"
            value={text}
            onChange={(e) => onText(e.target.value)}
            spellCheck={false}
            disabled={blocked}
            placeholder={version === null ? '尚無參數（載入失敗或仍在讀取）' : undefined}
          />
        </div>
      </details>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="params-save"
          disabled={blocked}
          className="rounded border border-border px-3 py-1.5 text-sm"
          onClick={() => void save()}
        >
          儲存並推送
        </button>
      </div>
    </div>
  );
}

function ParamFieldControl({
  field,
  params,
  disabled,
  onChange,
}: {
  field: ParamField;
  params: Record<string, unknown>;
  disabled: boolean;
  onChange: (next: Record<string, unknown>) => void;
}) {
  switch (field.kind) {
    case 'boolean':
      return (
        <fieldset className="flex flex-col gap-1" data-testid={`params-field-${field.key}`}>
          <legend className="text-sm">{field.label}</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              id={`param-${field.key}`}
              type="checkbox"
              checked={readBooleanFlag(params, field.key)}
              disabled={disabled}
              onChange={(event) => onChange(writeBooleanFlag(params, field.key, event.target.checked))}
            />
            開啟
          </label>
          <p className="text-xs text-text-tertiary">{field.hint}</p>
        </fieldset>
      );
    case 'platforms':
      return (
        <fieldset className="flex flex-col gap-2" data-testid={`params-field-${field.key}`}>
          <legend className="text-sm">{field.label}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {PLATFORM_IDS.map((platform) => (
              <label key={platform} className="flex items-center gap-2 text-sm">
                <input
                  id={`param-${field.key}-${platform}`}
                  data-testid={`params-platform-${field.key}-${platform}`}
                  type="checkbox"
                  checked={readPlatformFlag(params, field.key, platform)}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange(writePlatformFlag(params, field.key, platform, event.target.checked))
                  }
                />
                {PLATFORM_LABEL[platform]}
              </label>
            ))}
          </div>
          <p className="text-xs text-text-tertiary">{field.hint}</p>
        </fieldset>
      );
    default: {
      const neverField: never = field;
      return neverField;
    }
  }
}
